import React, { useState } from 'react';
import { Building2, ExternalLink, AlertTriangle } from 'lucide-react';
import { Input, Textarea } from '../../ui-v2';

export default function Paso1Info({ payload, errores, dispatch }) {
  const errorDe = (campo) => errores.find((e) => e.campo === campo)?.mensaje;
  const [confirmandoCambio, setConfirmandoCambio] = useState(null);

  // Cambio de ámbito: si hay técnicos o usuarios ya elegidos, pedir confirmación
  // antes de limpiarlos (porque son IDs de otra tabla).
  function intentarCambioAmbito(nuevo) {
    if (nuevo === payload.ambito) return;
    const hayPersonas =
      (payload.tecnicos_ids?.length || 0) +
      (payload.usuarios_ids?.length || 0) > 0;
    if (hayPersonas) {
      setConfirmandoCambio(nuevo);
    } else {
      dispatch({ type: 'CAMBIAR_AMBITO', ambito: nuevo });
    }
  }

  function confirmarCambio() {
    dispatch({ type: 'CAMBIAR_AMBITO', ambito: confirmandoCambio });
    setConfirmandoCambio(null);
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Selector de ámbito */}
      <Campo
        label="Ámbito de la campaña *"
        error={errorDe('ambito')}
        hint="Define a qué colectivo van dirigidas las intervenciones. No se puede mezclar."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <TarjetaAmbito
            icon={<Building2 className="w-4 h-4" />}
            label="Internos"
            descripcion="Empleados de la empresa"
            activo={payload.ambito === 'interno'}
            onClick={() => intentarCambioAmbito('interno')}
          />
          <TarjetaAmbito
            icon={<ExternalLink className="w-4 h-4" />}
            label="Colaboradores"
            descripcion="Personas externas"
            activo={payload.ambito === 'colaborador'}
            onClick={() => intentarCambioAmbito('colaborador')}
          />
        </div>
      </Campo>

      <Campo label="Título *" error={errorDe('titulo')} hint="Ej. «Mantenimiento equipos Q1»">
        <Input
          value={payload.titulo}
          onChange={(e) =>
            dispatch({ type: 'SET_FIELD', field: 'titulo', value: e.target.value })
          }
          placeholder="Nombre identificativo de la campaña"
          maxLength={255}
          autoFocus
        />
      </Campo>

      <Campo label="Descripción" error={errorDe('descripcion')}>
        <Textarea
          value={payload.descripcion || ''}
          onChange={(e) =>
            dispatch({ type: 'SET_FIELD', field: 'descripcion', value: e.target.value })
          }
          placeholder="Detalles para técnicos y personas convocadas"
          rows={3}
        />
      </Campo>

      {/* ─── Fase 15 (#5) · Información visible para el usuario final ─── */}
      <div className="mt-6 pt-6 border-t-2 border-dashed border-slate-200">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Información visible para el usuario
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Contenido que verá el usuario final (en Mis Citas y en las
            notificaciones). Déjalo vacío para usar el título y la descripción
            internos.
          </p>
        </div>

        <div className="space-y-5">
          <Campo
            label="Título para el usuario"
            hint="Vacío → se mostrará el título interno."
          >
            <Input
              value={payload.titulo_usuario ?? ''}
              onChange={(e) =>
                dispatch({ type: 'SET_FIELD', field: 'titulo_usuario', value: e.target.value })
              }
              placeholder="Ej. «Intervención técnica programada»"
              maxLength={255}
            />
          </Campo>

          <Campo
            label="Descripción para el usuario"
            hint="Vacío → se mostrará la descripción interna."
          >
            <Textarea
              value={payload.descripcion_usuario ?? ''}
              onChange={(e) =>
                dispatch({ type: 'SET_FIELD', field: 'descripcion_usuario', value: e.target.value })
              }
              placeholder="Texto orientado al usuario final (qué es, qué debe hacer, fecha límite…)"
              rows={3}
            />
          </Campo>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Campo
          label="Asignación de técnico"
          hint="«Libre»: la persona elige. «Automática»: el sistema reparte."
        >
          <div className="flex items-center gap-2">
            <RadioPill
              activo={payload.asignacion_modo === 'libre'}
              onClick={() =>
                dispatch({ type: 'SET_FIELD', field: 'asignacion_modo', value: 'libre' })
              }
            >
              Libre
            </RadioPill>
            <RadioPill
              activo={payload.asignacion_modo === 'automatica'}
              onClick={() =>
                dispatch({ type: 'SET_FIELD', field: 'asignacion_modo', value: 'automatica' })
              }
            >
              Automática
            </RadioPill>
          </div>
        </Campo>

        <Campo
          label="Máx. cancelaciones por persona"
          error={errorDe('max_cancelaciones_default')}
          hint="Veces que cada persona puede cancelar su cita en esta campaña."
        >
          <Input
            type="number"
            min={0}
            max={50}
            value={payload.max_cancelaciones_default}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'max_cancelaciones_default',
                value: parseInt(e.target.value, 10) || 0,
              })
            }
            className="w-28"
          />
        </Campo>

        <Campo
          label="Antelación mínima para cancelar (horas)"
          error={errorDe('horas_antelacion_cancelar')}
          hint="0 = sin restricción. P. ej. 24 = solo se puede cancelar hasta 24h antes."
        >
          <Input
            type="number"
            min={0}
            max={720}
            value={payload.horas_antelacion_cancelar || 0}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'horas_antelacion_cancelar',
                value: parseInt(e.target.value, 10) || 0,
              })
            }
            className="w-28"
          />
        </Campo>
      </div>

      {/* Modal de confirmación de cambio de ámbito */}
      {confirmandoCambio && (
        <ConfirmacionCambioAmbito
          ambitoActual={payload.ambito}
          ambitoNuevo={confirmandoCambio}
          cantidadAfectada={
            (payload.tecnicos_ids?.length || 0) + (payload.usuarios_ids?.length || 0)
          }
          onConfirmar={confirmarCambio}
          onCancelar={() => setConfirmandoCambio(null)}
        />
      )}
    </div>
  );
}

function TarjetaAmbito({ icon, label, descripcion, activo, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-left border rounded-lg p-3 flex items-start gap-3 transition-colors ' +
        (activo
          ? 'border-slate-900 bg-slate-900 text-white'
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400')
      }
    >
      <span className={'mt-0.5 ' + (activo ? '' : 'text-slate-400')}>{icon}</span>
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className={'block text-xs ' + (activo ? 'text-slate-300' : 'text-slate-400')}>
          {descripcion}
        </span>
      </span>
    </button>
  );
}

function Campo({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {error ? (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function RadioPill({ activo, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' +
        (activo
          ? 'bg-slate-900 text-white'
          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')
      }
    >
      {children}
    </button>
  );
}

function ConfirmacionCambioAmbito({
  ambitoActual,
  ambitoNuevo,
  cantidadAfectada,
  onConfirmar,
  onCancelar,
}) {
  const nombreAmbito = (a) => (a === 'interno' ? 'Internos' : 'Colaboradores');

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
      onClick={onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Cambiar ámbito de la campaña
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Vas a cambiar de <strong>{nombreAmbito(ambitoActual)}</strong> a{' '}
              <strong>{nombreAmbito(ambitoNuevo)}</strong>.
            </p>
          </div>
        </div>

        <p className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-md p-3 mb-4">
          Las {cantidadAfectada} {cantidadAfectada === 1 ? 'persona ya seleccionada' : 'personas ya seleccionadas'}{' '}
          se quitarán de la campaña: pertenecen al ámbito actual y no son válidas en
          el nuevo. Tendrás que volver a seleccionar técnicos y usuarios.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            Cambiar ámbito
          </button>
        </div>
      </div>
    </div>
  );
}
